import { Head, usePage } from '@inertiajs/react'

import EventLayout from '@/components/layouts/EventLayout'
import { PageHeader } from '@/components/patterns'
import { ActivityLogFilterBar } from '@/components/activity_logs/ActivityLogFilterBar'
import { ActivityLogTable } from '@/components/activity_logs/ActivityLogTable'
import type {
  ActivityLog,
  ActivityLogFilterOptions,
  ActivityLogFilters,
  ActivityLogPagination,
} from '@/components/activity_logs/types'

interface EventSummary {
  id?: number
  name?: string
  slug?: string
}

interface EventActivityLogsProps {
  event?: EventSummary
  logs: ActivityLog[]
  pagination: ActivityLogPagination
  filters: ActivityLogFilters
  filter_options: ActivityLogFilterOptions
}

interface SharedProps {
  /** Shared on every event page via `Events::BaseController#inertia_share`. */
  event_context?: EventSummary
  /** Inertia's `PageProps` constraint — the page also carries its own props. */
  [key: string]: unknown
}

/**
 * Event-scoped activity trail. `Events::ActivityLogsController` injects the event
 * restriction structurally, so no event column and no event filter here — every row on
 * the page already belongs to this event.
 */
export default function EventActivityLogsPage({
  event,
  logs,
  pagination,
  filters,
  filter_options,
}: EventActivityLogsProps) {
  const { props, url } = usePage<SharedProps>()

  const eventName = event?.name || props.event_context?.name || 'Event'
  const eventSlug = event?.slug || props.event_context?.slug || url.split('/')[2] || ''
  const basePath = `/events/${eventSlug}/activity_logs`

  return (
    <EventLayout>
      <Head title={`${eventName} - Activity Logs`} />

      <PageHeader
        title="Activity Logs"
        subtitle="Every recorded action for this event — who did it, from where, and what changed."
      />

      <section className="min-h-[500px] bg-[#f3f5f8] px-6 py-6">
        <div className="flex flex-col gap-4">
          <ActivityLogFilterBar
            basePath={basePath}
            filters={filters}
            filterOptions={filter_options}
            perPage={pagination.per_page}
          />

          <div className="rounded-lg bg-white p-4">
            <ActivityLogTable
              logs={logs}
              pagination={pagination}
              filters={filters}
              basePath={basePath}
            />
          </div>
        </div>
      </section>
    </EventLayout>
  )
}
